import React, { useState, useEffect } from 'react';
import { X, Package, Printer, AlertTriangle, Loader2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { pickingService, PackageDto } from '../../../../../services/pickingService';
import { printingService } from '../../../../../services/printingService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { useActiveReports } from '../../../../../hooks/useActiveReports';
import { ReportType } from '../../../../../services/reportConfigurationService';

interface BatchPackagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  onCancel: () => void;
  pListId: number;
  pListDescription?: string;
}

export const BatchPackagesModal: React.FC<BatchPackagesModalProps> = ({
  isOpen,
  onClose,
  onContinue,
  onCancel,
  pListId,
  pListDescription
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [packages, setPackages] = useState<PackageDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSkipWarning, setShowSkipWarning] = useState(false);
  const [labelsPrinted, setLabelsPrinted] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printResult, setPrintResult] = useState<{ success: boolean; message: string } | null>(null);

  // Pakkeetiketter skrives med Kollietikett-mal (ReportType 40) — samme mal som kolli inne i
  // plukket. Eske-ID-en (PListOrderPackages.PackageID) gir malen {{PListOrderPackages.*}};
  // plukkliste + ordre gir Orders/OrderLine-data (TW 36438546). Valgt mal sendes eksplisitt;
  // forhåndsvalg fra «Standard kolli-etikett-mal» (DefaultReportId:40), ellers første aktive.
  const { reports: labelReports, selectedReportId, setSelectedReportId } =
    useActiveReports(ReportType.PackageLabel);

  useEffect(() => {
    if (isOpen && pListId) {
      loadPackages();
    }
  }, [isOpen, pListId]);

  const loadPackages = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pickingService.getPackagesForPList(pListId);
      setPackages(data);
    } catch (err: any) {
      console.error('Failed to load packages:', err);
      setError(t('warehouse.modules.picking.batchPackages.loadError', { 0: err.message }));
    } finally {
      setLoading(false);
    }
  };

  const handlePrintLabels = async () => {
    if (!user?.companyNo) {
      setPrintResult({ success: false, message: t('warehouse.modules.picking.batchPackages.missingUserInfo') });
      return;
    }

    setIsPrinting(true);
    setPrintResult(null);

    try {
      // Én kollietikett per pakke. Etikettdata hentes fra tabellene via konteksten
      // (eske + plukkliste + ordre).
      let labelsSent = 0;
      let lastError: string | undefined;
      for (const pkg of packages) {
        const result = await printingService.printPackageLabel(user.companyNo, {
          packageId: pkg.packageNo,
          pListId,
          orderNo: pkg.orderNo ?? null,
          reportId: selectedReportId
        });
        if (result.success) {
          labelsSent += result.labelsPrinted ?? 1;
        } else {
          lastError = result.message;
        }
      }

      if (labelsSent > 0 && !lastError) {
        setLabelsPrinted(true);
        setPrintResult({
          success: true,
          message: labelsSent === 1
            ? t('warehouse.modules.picking.batchPackages.labelsSentSingular', { 0: labelsSent })
            : t('warehouse.modules.picking.batchPackages.labelsSentPlural', { 0: labelsSent })
        });
      } else {
        setPrintResult({
          success: false,
          message: lastError || t('warehouse.modules.picking.batchPackages.unknownPrintError')
        });
      }
    } catch (err: any) {
      console.error('Failed to print batch package labels:', err);
      setPrintResult({
        success: false,
        message: err.message || t('warehouse.modules.picking.batchPackages.printError')
      });
    } finally {
      setIsPrinting(false);
    }
  };

  const handleContinue = () => {
    if (!labelsPrinted && packages.length > 0) {
      setShowSkipWarning(true);
    } else {
      onContinue();
    }
  };

  const handleConfirmSkip = () => {
    setShowSkipWarning(false);
    onContinue();
  };

  const handleCancel = () => {
    onCancel();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-2xl overflow-hidden flex flex-col sm:max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Package className="text-orange-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{t('warehouse.modules.picking.batchPackages.title')}</h2>
              <p className="text-sm text-gray-500">
                {t('warehouse.modules.picking.batchPackages.pickListLabel')} #{pListId} {pListDescription && `- ${pListDescription}`}
              </p>
            </div>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-blue-600" size={32} />
              <span className="ml-3 text-gray-600">{t('warehouse.modules.picking.batchPackages.loadingPackages')}</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-red-600 flex-shrink-0" size={20} />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          ) : packages.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-yellow-600 flex-shrink-0" size={20} />
                <div>
                  <p className="text-sm font-medium text-yellow-900">{t('warehouse.modules.picking.batchPackages.noPackagesTitle')}</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    {t('warehouse.modules.picking.batchPackages.noPackagesDesc')}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Info box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>{t('warehouse.modules.picking.batchPackages.infoBoxLabel')}</strong> {t('warehouse.modules.picking.batchPackages.infoBoxText')}
                  {' '}
                  {packages.length === 1
                    ? t('warehouse.modules.picking.batchPackages.packageCountSingular', { 0: packages.length })
                    : t('warehouse.modules.picking.batchPackages.packageCountPlural', { 0: packages.length })}
                </p>
              </div>

              {/* Packages table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('common.fields.type')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('common.fields.description')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.general.carrier')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {packages.map((pkg, index) => (
                      <tr key={pkg.packageNo} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                          {pkg.visiblePackageNo ?? index + 1}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {pkg.packagingTypeDescription || t('warehouse.modules.picking.batchPackages.typeFallback', { 0: pkg.packagingTypeNo })}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div>{pkg.description1 || '-'}</div>
                          {pkg.description2 && (
                            <div className="text-xs text-gray-400">{pkg.description2}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {pkg.carrierName || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mal-velger — vises kun når tenanten har flere aktive etikettmaler av typen */}
              {labelReports.length > 1 && !labelsPrinted && (
                <div className="mt-4 flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    {t('warehouse.modules.picking.batchPackages.labelTemplate', {}, 'Etikettmal:')}
                  </label>
                  <select
                    value={selectedReportId ?? ''}
                    onChange={(e) => setSelectedReportId(e.target.value ? Number(e.target.value) : undefined)}
                    className="px-2 py-1.5 border border-gray-300 rounded text-sm bg-white"
                  >
                    {labelReports.map((r) => (
                      <option key={r.reportID} value={r.reportID}>{r.description}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Print result status */}
              {printResult && (
                <div className={`mt-4 border rounded-lg p-4 ${
                  printResult.success
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}>
                  <p className={`text-sm flex items-center gap-2 ${
                    printResult.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {printResult.success ? (
                      <CheckCircle2 size={16} />
                    ) : (
                      <AlertTriangle size={16} />
                    )}
                    {printResult.message}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Skip warning dialog */}
          {showSkipWarning && (
            <div className="mt-4 bg-yellow-50 border border-yellow-300 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-yellow-600 flex-shrink-0" size={24} />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-yellow-900">
                    {t('warehouse.modules.picking.batchPackages.skipWarningTitle')}
                  </h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    {t('warehouse.modules.picking.batchPackages.skipWarningDesc')}
                  </p>
                  <div className="mt-3 flex gap-3">
                    <button
                      onClick={() => setShowSkipWarning(false)}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      {t('common.buttons.back')}
                    </button>
                    <button
                      onClick={handleConfirmSkip}
                      className="px-3 py-2 text-sm font-medium text-yellow-800 bg-yellow-100 border border-yellow-300 rounded-lg hover:bg-yellow-200"
                    >
                      {t('warehouse.modules.picking.batchPackages.continueWithoutLabels')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleCancel}
            disabled={loading}
            className="px-4 py-3 sm:py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {t('common.buttons.cancel')}
          </button>

          <div className="flex flex-col sm:flex-row gap-3">
            {packages.length > 0 && !labelsPrinted && (
              <button
                onClick={handlePrintLabels}
                disabled={loading || showSkipWarning || isPrinting}
                className="px-4 py-3 sm:py-2 min-h-[44px] text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isPrinting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {t('common.status.printing')}
                  </>
                ) : (
                  <>
                    <Printer size={16} />
                    {t('warehouse.modules.picking.batchPackages.printLabels', { 0: packages.length })}
                  </>
                )}
              </button>
            )}

            <button
              onClick={handleContinue}
              disabled={loading || showSkipWarning || isPrinting}
              className="px-4 py-3 sm:py-2 min-h-[44px] text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {t('warehouse.modules.picking.batchPackages.continueToPicking')}
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchPackagesModal;
